import React, { FunctionComponent } from 'react';
import { Box } from '@mui/material';

import { getLayoutModelsWithPendingChanges } from '@paragon/layout-models';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { archiveProject } from '../../store/actions/projects';
import { useAppDispatch, useAppSelector } from '../hooks';

interface Props {
  readonly projectGuids: readonly string[];
}

const MAX_LISTED_PROJECTS = 5;

export const ArchiveConfirmationDialog: FunctionComponent<Props> = ({ projectGuids }) => {
  const dispatch = useAppDispatch();
  const layoutModels = useAppSelector(getLayoutModelsWithPendingChanges);

  const projects = projectGuids
    .map((guid) => ({ guid, name: layoutModels[guid]?.name }))
    .filter((project): project is { guid: string; name: string } => project.name != null);
  const listedProjects = projects.slice(0, MAX_LISTED_PROJECTS);
  const overflowCount = projects.length - listedProjects.length;
  const isSingle = projectGuids.length === 1;

  return (
    <Dialog
      title={
        isSingle ? 'Are you sure you want to archive this project?' : 'Are you sure you want to archive these projects?'
      }
      submitText="Archive"
      onSubmit={async () => {
        try {
          await Promise.all(projectGuids.map((projectGuid) => dispatch(archiveProject(projectGuid))));
        } finally {
          dispatch(closeCurrentPopup());
        }
      }}
      onCancel="close"
    >
      {listedProjects.length > 0 && (
        <Box component="ul" sx={{ m: 0, mb: 1, pl: 3 }}>
          {listedProjects.map(({ guid, name }) => (
            <li key={guid}>Project {name}</li>
          ))}
          {overflowCount > 0 && <li>…and {overflowCount} more</li>}
        </Box>
      )}
      After {isSingle ? 'this project is' : 'these projects are'} archived, you won&apos;t be able to view{' '}
      {isSingle ? 'it' : 'them'} again without contacting Paragon Customer Support.
    </Dialog>
  );
};
